import { useEffect, useState } from "react";
import { ApiError, type DashboardView, type DayTakings, api, formatMoney } from "./api";
import { shortDay } from "./dates";
import { PageHeader } from "./layout/Layout";
import { go } from "./layout/router";
import { Alert, Box, Empty, Loading, StatCard, TwoLine } from "./ui";

/**
 * The screen somebody opens in the morning (quotation A21).
 *
 * It answers what OHC ask each other: what did we take, what is on the shelf, what has
 * not been paid, and what is waiting for somebody to do something about it. Every figure
 * links to the page it came from, so nobody has to remember where a number lives.
 */
export function Dashboard({ fullName }: { readonly fullName: string }): React.ReactElement {
  const [view, setView] = useState<DashboardView | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let live = true;
    api
      .dashboard()
      .then((result) => {
        if (live) setView(result.dashboard);
      })
      .catch((problem: unknown) => {
        if (live) setError(problem instanceof ApiError ? problem.message : "Could not load the dashboard");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  if (loading) return <Loading />;
  if (!view) return <Alert tone="danger">{error ?? "Could not load the dashboard"}</Alert>;

  const corrections = view.waiting.corrections ?? 0;
  const paynowOpen = view.waiting.paynowOpen ?? 0;
  const jobs =
    view.waiting.toCheck + view.waiting.toFinish + view.waiting.buybacks + view.waiting.conflicts + corrections + paynowOpen;

  return (
    <>
      <PageHeader title={`Good day, ${fullName.split(/\s+/)[0] ?? fullName}`} subtitle={longDay(view.today)} />

      {error ? <Alert tone="danger">{error}</Alert> : null}

      <div className="grid grid-cols-12 gap-x-6">
        <div className="xl:col-span-3 md:col-span-6 col-span-12">
          <StatCard
            label="Payments received today"
            value={formatMoney(view.takings.todayCents)}
            icon="ti ti-cash"
            tone="primary"
            note={`${formatMoney(view.takings.monthCents)} this month`}
          />
        </div>
        <div className="xl:col-span-3 md:col-span-6 col-span-12">
          <StatCard
            label="Outstanding customer payments"
            value={formatMoney(view.takings.unpaidCents)}
            icon="ti ti-hourglass"
            tone={view.takings.unpaidCount > 0 ? "warning" : "success"}
            note={
              view.takings.unpaidCount > 0
                ? `${view.takings.unpaidCount} unpaid ${view.takings.unpaidCount === 1 ? "invoice" : "invoices"}`
                : "All sales are paid"
            }
            {...(view.takings.unpaidCount > 0 ? { noteTone: "warning" as const } : {})}
          />
        </div>
        {view.money ? (
          <div className="xl:col-span-3 md:col-span-6 col-span-12">
            <StatCard
              label="Bank balance"
              value={formatMoney(view.money.bankCents)}
              icon="ti ti-building-bank"
              tone="info"
              note={
                view.money.owedCount > 0
                  ? `${formatMoney(view.money.owedCents)} due on ${view.money.owedCount} unpaid payment ${view.money.owedCount === 1 ? "voucher" : "vouchers"}`
                  : "No unpaid payment vouchers"
              }
            />
          </div>
        ) : null}
        {view.stock ? (
          <div className="xl:col-span-3 md:col-span-6 col-span-12">
            <StatCard
              label="Inventory value at cost"
              value={formatMoney(view.stock.companyCents)}
              icon="ti ti-building-store"
              tone={view.stock.belowZero > 0 ? "warning" : "secondary"}
              note={
                view.stock.belowZero > 0
                  ? `${view.stock.belowZero} with negative inventory, flagged for review`
                  : `${view.stock.companyItems} items in inventory`
              }
              {...(view.stock.belowZero > 0 ? { noteTone: "warning" as const } : {})}
            />
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-12 gap-x-6">
        <div className="xl:col-span-8 col-span-12">
          <Box title="Daily payments received - last 14 days">
            <Takings days={view.days} />
          </Box>
        </div>
        <div className="xl:col-span-4 col-span-12">
          <Box title="Pending tasks" count={jobs}>
            {jobs === 0 ? (
              <Empty icon="ri-checkbox-circle-line">No pending tasks. All records are up to date.</Empty>
            ) : (
              <ul className="list-none m-0 p-0 divide-y divide-defaultborder dark:divide-defaultborder/10">
                {paynowOpen > 0 ? (
                  <Job
                    tone="warning"
                    icon="ri-qr-code-line"
                    what={`${paynowOpen} unmatched PayNow ${paynowOpen === 1 ? "payment" : "payments"}`}
                    where="Sales and buybacks - PayNow payments received"
                    goLabel="Sales and buybacks, PayNow received"
                    onGo={() => go("sales")}
                  />
                ) : null}
                {corrections > 0 ? (
                  <Job
                    tone="warning"
                    icon="ri-question-line"
                    what={`${corrections} sale correction ${corrections === 1 ? "request" : "requests"}`}
                    where="Sales and buybacks"
                    onGo={() => go("sales")}
                  />
                ) : null}
                {view.waiting.conflicts > 0 ? (
                  <Job
                    tone="danger"
                    icon="ri-error-warning-line"
                    what={`${view.waiting.conflicts} ${view.waiting.conflicts === 1 ? "conflict requires" : "conflicts require"} review`}
                    where="Sales and buybacks"
                    onGo={() => go("sales")}
                  />
                ) : null}
                {view.waiting.buybacks > 0 ? (
                  <Job
                    tone="warning"
                    icon="ri-hand-coin-line"
                    what={`${view.waiting.buybacks} customer ${view.waiting.buybacks === 1 ? "buyback is" : "buybacks are"} awaiting inventory entry`}
                    where="Sales and buybacks"
                    onGo={() => go("sales")}
                  />
                ) : null}
                {view.waiting.toFinish > 0 ? (
                  <Job
                    tone="warning"
                    icon="ri-draft-line"
                    what={`${view.waiting.toFinish} incomplete inventory ${view.waiting.toFinish === 1 ? "record" : "records"}`}
                    where="Inventory - Incomplete records"
                    goLabel="Stock, To finish"
                    onGo={() => go("stock")}
                  />
                ) : null}
                {view.waiting.toCheck > 0 ? (
                  <Job
                    tone="info"
                    icon="ri-check-double-line"
                    what={`${view.waiting.toCheck} ${view.waiting.toCheck === 1 ? "record" : "records"} awaiting verification`}
                    where="Staff balances - Pending tasks"
                    goLabel="People, Still to do"
                    onGo={() => go("people")}
                  />
                ) : null}
                {view.money && view.money.unmatchedLines > 0 ? (
                  <Job
                    tone="info"
                    icon="ri-bank-line"
                    what={`${view.money.unmatchedLines} unmatched bank ${view.money.unmatchedLines === 1 ? "transaction" : "transactions"}`}
                    where="Bank reconciliation"
                    goLabel="Bank"
                    onGo={() => go("bank")}
                  />
                ) : null}
              </ul>
            )}
          </Box>
        </div>
      </div>

      <Box title="Recent activity">
        {view.recent.length === 0 ? (
          <Empty icon="ri-time-line">No recent activity.</Empty>
        ) : (
          <ul className="list-none m-0 p-0 divide-y divide-defaultborder dark:divide-defaultborder/10">
            {view.recent.map((entry) => (
              <li key={`${entry.at}-${entry.what}`} className="py-2 flex items-center gap-3">
                <TwoLine main={entry.who ? `${entry.who} ${entry.what}` : /^[A-Z]/.test(entry.what) ? entry.what : `Somebody ${entry.what}`} />
                <div className="flex-1" />
                <span className="text-[0.75rem] text-[#8c9097] dark:text-white/50">{shortWhen(entry.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Box>
    </>
  );
}

function longDay(day: string): string {
  return new Date(`${day}T00:00:00`).toLocaleDateString("en-SG", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function shortWhen(at: string): string {
  return new Date(at).toLocaleString("en-SG", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

/**
 * Takings by day: one bar a day, the quiet days kept in so a gap reads as a gap.
 *
 * One measure, so one colour and no legend; the highest day and the last day carry their
 * own figure, and hovering any bar gives that day in full. The axis is drawn from the
 * data rather than a library, which keeps the page to the template's own look and adds
 * nothing to download.
 */
function Takings({ days }: { readonly days: readonly DayTakings[] }): React.ReactElement {
  const [over, setOver] = useState<number | undefined>();

  const most = Math.max(1, ...days.map((day) => day.soldCents));
  const total = days.reduce((sum, day) => sum + day.soldCents, 0);
  const busiest = days.reduce((best, day) => (day.soldCents > best.soldCents ? day : best), days[0] as DayTakings);

  if (days.length === 0 || total === 0) {
    return <Empty icon="ri-bar-chart-line">No sales in the last 14 days.</Empty>;
  }

  return (
    <div>
      <div className="flex items-baseline gap-3 mb-4">
        <span className="text-[1.5rem] font-semibold">{formatMoney(total)}</span>
        <span className="text-[0.813rem] text-[#8c9097] dark:text-white/50">
          over {days.length} days · highest {formatMoney(busiest.soldCents)} on {shortDay(busiest.day)}
        </span>
      </div>

      <div className="flex items-end gap-[2px] h-40" role="img" aria-label="Takings for each of the last fourteen days">
        {days.map((day, at) => {
          const height = Math.round((day.soldCents / most) * 100);
          return (
            <button
              key={day.day}
              type="button"
              className="relative flex-1 h-full flex flex-col justify-end group"
              aria-label={`${shortDay(day.day)}: ${formatMoney(day.soldCents)}`}
              onMouseEnter={() => setOver(at)}
              onMouseLeave={() => setOver(undefined)}
              onFocus={() => setOver(at)}
              onBlur={() => setOver(undefined)}
            >
              {over === at ? (
                <span className="absolute bottom-full mb-1 start-1/2 -translate-x-1/2 whitespace-nowrap rounded-sm bg-defaulttextcolor text-white dark:bg-white dark:text-defaulttextcolor px-2 py-1 text-[0.6875rem] z-10">
                  {shortDay(day.day)} · {formatMoney(day.soldCents)}
                  {day.sales > 0 ? ` · ${day.sales} ${day.sales === 1 ? "sale" : "sales"}` : ""}
                </span>
              ) : null}
              <span
                className={`block w-full rounded-t-[4px] transition-[height] ${
                  day.soldCents === 0 ? "bg-defaultborder dark:bg-white/10" : "bg-primary"
                } ${over === at ? "opacity-100" : "opacity-90"}`}
                style={{ height: `${day.soldCents === 0 ? 2 : Math.max(height, 4)}%` }}
              />
            </button>
          );
        })}
      </div>

      <div className="flex gap-[2px] mt-2">
        {days.map((day, at) => (
          <span
            key={day.day}
            className={`flex-1 text-center text-[0.6875rem] ${
              at === days.length - 1 ? "text-defaulttextcolor font-semibold" : "text-[#8c9097] dark:text-white/50"
            }`}
          >
            {shortDay(day.day)}
          </span>
        ))}
      </div>
    </div>
  );
}

// Written out rather than built from the tone, because Tailwind keeps only the class
// names it can see in the source.
const JOB_TONES: Record<"danger" | "warning" | "info", string> = {
  danger: "!bg-danger/10 !text-danger",
  warning: "!bg-warning/10 !text-warning",
  info: "!bg-info/10 !text-info",
};

/** One thing waiting, and the page where it is done. */
function Job({
  tone,
  icon,
  what,
  where,
  goLabel = where,
  onGo,
}: {
  readonly tone: "danger" | "warning" | "info";
  readonly icon: string;
  readonly what: string;
  readonly where: string;
  /**
   * The arrow's accessible name, when it predates the visible wording: the walk-through
   * scripts find buttons by these names, so they stay as they were.
   */
  readonly goLabel?: string;
  readonly onGo: () => void;
}): React.ReactElement {
  return (
    <li className="py-3 flex items-center gap-3">
      <span className={`avatar avatar-sm avatar-rounded ${JOB_TONES[tone]} !mb-0`}>
        <i className={`${icon} text-[1rem]`} />
      </span>
      <TwoLine main={what} sub={where} />
      <div className="flex-1" />
      <button type="button" aria-label={`Go to ${goLabel}`} className="ti-btn ti-btn-icon ti-btn-light !mb-0" onClick={onGo}>
        <i className="ri-arrow-right-line" />
      </button>
    </li>
  );
}

